import React, { useEffect, useMemo, useRef } from "react";
import "./PaymentAllocationEditor.css";

export const paymentModes = ["CASH", "CARD", "UPI", "OTHER"];

export const createPaymentAllocation = (amount = 0) => ({
  paymentMode: "",
  amount: Number(amount || 0),
  tenderedAmount: Number(amount || 0),
  referenceNumber: ""
});

export const summarizePayments = (payments = []) => {
  const valid = payments.filter(payment => payment.paymentMode && Number(payment.amount) > 0);
  const modes = [...new Set(valid.map(payment => payment.paymentMode))];
  return {
    payments: valid.map(payment => ({
      paymentMode: payment.paymentMode,
      amount: Number(payment.amount),
      tenderedAmount: Number(payment.tenderedAmount || payment.amount),
      referenceNumber: payment.referenceNumber?.trim() || null
    })),
    paymentMode: modes.length > 1 ? "MIXED" : (modes[0] || ""),
    settlementAmount: valid.reduce((sum, payment) => sum + Number(payment.tenderedAmount || payment.amount || 0), 0),
    changeReturn: valid.reduce((sum, payment) => sum + Math.max(0, Number(payment.tenderedAmount || 0) - Number(payment.amount || 0)), 0)
  };
};

export const getPaymentValidationError = (payments = [], payableAmount = 0) => {
  if (payments.length === 0 || payments.some(payment => !payment.paymentMode)) {
    return "Please select a payment method";
  }
  if (payments.some(payment => Number(payment.amount) <= 0)) {
    return "Payment amount must be greater than zero";
  }
  if (payments.some(payment => payment.paymentMode === "CASH" && Number(payment.tenderedAmount) < Number(payment.amount))) {
    return "Tendered amount cannot be less than allocated amount";
  }
  const allocated = payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  if (Math.abs(allocated - Number(payableAmount || 0)) > 0.001) {
    return "Payment allocations must equal the payable amount";
  }
  return null;
};

const PaymentAllocationEditor = ({ payableAmount, payments, onChange, disabled = false }) => {
  const previousPayable = useRef(Number(payableAmount || 0));
  const payable = Number(payableAmount || 0);

  useEffect(() => {
    const previous = previousPayable.current;
    previousPayable.current = payable;
    if (payments.length !== 1) return;
    const current = payments[0];
    if (Number(current.amount) !== previous && Number(current.amount) !== 0) return;
    onChange([{ ...current, amount: payable, tenderedAmount: current.paymentMode === "CASH" ? Math.max(payable, Number(current.tenderedAmount || 0)) : payable }]);
  }, [payable]); // eslint-disable-line react-hooks/exhaustive-deps

  const allocated = useMemo(() => payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0), [payments]);
  const remaining = Number((payable - allocated).toFixed(2));
  const selectedModes = useMemo(() => payments.map(payment => payment.paymentMode).filter(Boolean), [payments]);

  const update = (index, field, value) => {
    onChange(payments.map((payment, row) => {
      if (row !== index) return payment;
      const next = { ...payment, [field]: value };
      if (field === "paymentMode" && value !== "CASH") next.tenderedAmount = Number(next.amount || 0);
      if (field === "amount" && next.paymentMode !== "CASH") next.tenderedAmount = Number(value || 0);
      return next;
    }));
  };

  const addPayment = () => {
    if (payments.length >= paymentModes.length) return;
    onChange([...payments, createPaymentAllocation(Math.max(remaining, 0))]);
  };

  const removePayment = index => onChange(payments.filter((_, row) => row !== index));

  return (
    <section className={`payment-allocation ${payments.length > 1 ? "is-split" : "is-single"}`} aria-label="Payment allocation">
      <div className="payment-allocation__heading">
        <div><strong>Payment methods</strong><small>Split the payable amount across one or more methods.</small></div>
        <button type="button" onClick={addPayment} disabled={disabled || payments.length >= paymentModes.length}>
          <span className="payment-allocation__add-desktop">+ Add payment</span>
          <span className="payment-allocation__add-mobile">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 7h10m0 0-3-3m3 3-3 3M17 17H7m0 0 3 3m-3-3 3-3" />
            </svg>
            Split payment
          </span>
        </button>
      </div>

      {payments.map((payment, index) => (
        <div className={`payment-allocation__row payment-mode--${(payment.paymentMode || "").toLowerCase() || "unset"}`} key={index}>
          <div className="payment-allocation__mobile-row-heading">
            <strong>{payments.length > 1 ? `Payment ${index + 1}` : "Choose payment method"}</strong>
          </div>
          <div className="payment-allocation__mobile-methods" role="group" aria-label={payments.length > 1 ? `Payment method ${index + 1}` : "Payment method"}>
            {paymentModes.map(mode => {
              const isActive = payment.paymentMode === mode;
              const isUsedElsewhere = !isActive && selectedModes.includes(mode);
              return <button type="button" key={mode} className={isActive ? "active" : ""}
                disabled={disabled || isUsedElsewhere} aria-pressed={isActive}
                onClick={() => update(index, "paymentMode", mode)}>
                {mode === "UPI" ? "UPI" : mode[0] + mode.slice(1).toLowerCase()}
              </button>;
            })}
          </div>
          <label className="payment-allocation__method-field">Method
            <select aria-label={index === 0 ? "Payment method" : `Payment method ${index + 1}`} value={payment.paymentMode} disabled={disabled}
              onChange={event => update(index, "paymentMode", event.target.value)}>
              <option value="">Select method</option>
              {paymentModes.map(mode => <option key={mode} value={mode}>{mode === "UPI" ? "UPI" : mode[0] + mode.slice(1).toLowerCase()}</option>)}
            </select>
          </label>
          <label className="payment-allocation__amount-field">Allocated amount
            <input aria-label={index === 0 ? "Allocated Amount" : `Allocated Amount ${index + 1}`} type="number" min="0.01" step="0.01"
              value={payment.amount} disabled={disabled} onChange={event => update(index, "amount", event.target.value)} />
          </label>
          <label className="payment-allocation__received-field">
            <span className="payment-allocation__received-desktop">Received amount</span>
            <span className="payment-allocation__received-mobile">Cash Received</span>
            <input aria-label={index === 0 ? "Settlement Amount" : `Settlement Amount ${index + 1}`} type="number" min={payment.amount || 0} step="0.01"
              value={payment.tenderedAmount} disabled={disabled || payment.paymentMode !== "CASH"}
              onChange={event => update(index, "tenderedAmount", event.target.value)} />
          </label>
          <label className="payment-allocation__reference-field">Reference
            <input type="text" maxLength="100" value={payment.referenceNumber} disabled={disabled || !["CARD", "UPI", "OTHER"].includes(payment.paymentMode)}
              placeholder="Optional reference" onChange={event => update(index, "referenceNumber", event.target.value)} />
          </label>
          {payments.length > 1 && <button type="button" className="payment-allocation__remove" onClick={() => removePayment(index)} disabled={disabled}>Remove</button>}
        </div>
      ))}

      {payments.length === 1 && payments[0]?.paymentMode === "CASH" && Number(payments[0].tenderedAmount || 0) > Number(payments[0].amount || 0) && (
        <div className="payment-allocation__change">
          <span>Change to return</span>
          <strong>₹{(Number(payments[0].tenderedAmount) - Number(payments[0].amount)).toFixed(2)}</strong>
        </div>
      )}

      <div className={`payment-allocation__summary ${remaining === 0 ? "is-balanced" : ""}`}>
        <span>Payable <strong>₹{payable.toFixed(2)}</strong></span>
        <span>Allocated <strong>₹{allocated.toFixed(2)}</strong></span>
        <span>Remaining <strong>₹{remaining.toFixed(2)}</strong></span>
      </div>
    </section>
  );
};

export default PaymentAllocationEditor;
